/**
 * Les adresses écrites dans un texte libre, et la copie hors contexte sécurisé.
 *
 * Deux besoins nés du même constat : une recette collée dans le texte d'une note restait du
 * texte. Le lien ne s'ouvrait pas, et il n'y avait pas davantage moyen de le copier — le site
 * étant servi en clair (point ouvert P1), `navigator.clipboard` n'existe pas sur le téléphone.
 */

/**
 * Une adresse, ou du texte ordinaire. Un texte se rend en enchaînant ces fragments.
 */
export type Fragment = {
  readonly texte: string
  /** Non nulle : ce fragment est une adresse, à rendre comme un lien. */
  readonly lien: string | null
}

/**
 * Ponctuation qui termine une phrase, pas une adresse.
 *
 * « Voir https://exemple.fr/gateau. » : le point final appartient à la phrase. L'inclure mènerait
 * à une page introuvable — et l'erreur ne se voit qu'en cliquant.
 */
const PONCTUATION_FINALE = /[.,;:!?»"']+$/

const ADRESSE = /https?:\/\/[^\s<>"'«»]+/gi

/**
 * Isole les adresses d'un texte libre.
 *
 * <p>
 * Les parenthèses fermantes non appariées sont rendues au texte : « (voir https://exemple.fr) »
 * ne doit pas emporter la parenthèse dans l'adresse. Celles qui sont appariées restent, car
 * certaines adresses en contiennent — les articles encyclopédiques, notamment.
 * </p>
 */
export function decouperEnLiens(texte: string): readonly Fragment[] {
  const fragments: Fragment[] = []
  let position = 0

  for (const trouvee of texte.matchAll(ADRESSE)) {
    const debut = trouvee.index
    let adresse = trouvee[0]

    // La ponctuation finale, puis une parenthèse orpheline : dans cet ordre, pour attraper
    // « (https://exemple.fr/page). » en entier.
    adresse = adresse.replace(PONCTUATION_FINALE, '')

    while (
      adresse.endsWith(')') &&
      adresse.split(')').length > adresse.split('(').length
    ) {
      adresse = adresse.slice(0, -1)
    }

    adresse = adresse.replace(PONCTUATION_FINALE, '')

    if (adresse.length === 0) {
      continue
    }

    if (debut > position) {
      fragments.push({ texte: texte.slice(position, debut), lien: null })
    }

    fragments.push({ texte: adresse, lien: adresse })
    position = debut + adresse.length
  }

  if (position < texte.length) {
    fragments.push({ texte: texte.slice(position), lien: null })
  }

  return fragments
}

/** La première adresse d'un texte, s'il y en a une. */
export function premierLien(texte: string | null | undefined): string | null {
  if (!texte) {
    return null
  }

  return decouperEnLiens(texte).find((fragment) => fragment.lien !== null)?.lien ?? null
}

/**
 * Copie un texte dans le presse-papier.
 *
 * <p>
 * `navigator.clipboard` n'existe qu'en contexte sécurisé, et le site est servi en clair : sur le
 * téléphone, il est absent. D'où le repli sur `execCommand`, obsolète mais toujours implémenté
 * partout, et seul moyen de copier ici. Le jour où le point ouvert P1 sera traité, le premier
 * chemin reprendra la main tout seul.
 * </p>
 *
 * @returns vrai si la copie a réussi — un bouton qui ne dit rien vaut mieux qu'un bouton qui ment.
 */
export async function copier(texte: string): Promise<boolean> {
  if (typeof navigator !== 'undefined' && typeof navigator.clipboard?.writeText === 'function') {
    try {
      await navigator.clipboard.writeText(texte)
      return true
    } catch {
      // Refus de l'utilisateur ou contexte inattendu : on tente quand même le repli.
    }
  }

  if (typeof document === 'undefined') {
    return false
  }

  const champ = document.createElement('textarea')
  champ.value = texte
  // Hors de l'écran plutôt que masqué : un champ en `display: none` ne peut pas être sélectionné,
  // et la copie échouerait sans rien dire.
  champ.setAttribute('readonly', '')
  champ.style.position = 'fixed'
  champ.style.top = '-1000px'
  champ.style.opacity = '0'

  document.body.appendChild(champ)

  try {
    champ.select()
    champ.setSelectionRange(0, texte.length)

    return document.execCommand('copy')
  } catch {
    return false
  } finally {
    document.body.removeChild(champ)
  }
}
